<script setup>
/**
 * You & {name} (moved from the Mirror, 2026-10-29): a kept Our Stars report,
 * opened from its card under "Kept, and you & them". The day page's frame,
 * the report as it was kept, and the way back.
 */
import JournalGlyph from './JournalGlyph.vue'
import { shortDate } from '@/utils/journal-book'

defineProps({
  stars: { type: Object, default: null },        // one of normalizeOurStars
  desktop: { type: Boolean, default: false },
  calOpen: { type: Boolean, default: false },
  backLabel: { type: String, default: '' },
})
const emit = defineEmits(['cal', 'back'])
</script>

<template>
  <div class="sv" :class="{ desk: desktop }" data-testid="journal-stars">
    <header class="ph">
      <div class="phrow">
        <div class="wk">Your stars, side by side</div>
        <div class="tools">
          <button v-if="!desktop" type="button" class="ic" aria-label="Contents" title="Contents" :aria-expanded="calOpen" data-testid="journal-cal-btn" @click.stop="emit('cal')"><JournalGlyph name="contents" /></button>
          <button type="button" class="bk" data-testid="journal-view-back" @click="emit('back')">‹ {{ backLabel || 'Back' }}</button>
        </div>
      </div>
      <h2 class="dt">You &amp; {{ stars ? stars.name : '' }}</h2>
      <p v-if="stars && stars.key" class="meta">Kept {{ shortDate(stars.key) }}</p>
      <svg class="flour" viewBox="0 0 320 10" preserveAspectRatio="none" aria-hidden="true"><path class="ink" pathLength="1" stroke-width="1.2" d="M2 6C60 2 120 8 170 5s110-4 148-1" /></svg>
    </header>
    <p v-if="stars" class="rep">{{ stars.report }}</p>
    <!-- The three lines with Try this (recipient polish §6), when the words are the new ones. -->
    <ol v-if="stars && stars.lines && stars.lines.length" class="lines" data-testid="journal-stars-lines">
      <li v-for="l in stars.lines" :key="l.n">
        <b>{{ l.heading }}</b>
        <span>{{ l.picture || l.sentence + '.' }}</span>
        <span v-if="l.tryThis" class="try"><i>Try this:</i> {{ l.tryThis }}</span>
      </li>
    </ol>
  </div>
</template>

<style scoped>
.sv { color: var(--j-ink); }
.ph { position: sticky; top: 0; z-index: 5; margin: 0 -16px 0 -18px; padding: 10px 16px 2px 18px; background: #f2e9d6; }
.desk .ph { margin: 0 -30px; padding: 16px 30px 2px; }
.phrow { display: flex; align-items: center; justify-content: space-between; gap: 10px; min-height: 34px; padding-right: 30px; }
.desk .phrow { padding-right: 40px; }
.tools { display: flex; align-items: center; gap: 4px; }
.wk { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font: 600 10.5px var(--j-display); letter-spacing: .16em; text-transform: uppercase; color: var(--j-hand); }
.meta { margin: 4px 0 0; font: 500 13px var(--j-ui); color: var(--j-ink3); }
.dt { margin: 0; font: 600 31px/1.08 var(--j-serif); color: var(--j-ink); }
.desk .dt { font-size: 34px; }
.ic { width: 40px; height: 40px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; color: var(--j-ink2); background: none; border: 0; padding: 0; cursor: pointer; }
.ic :deep(svg) { width: 19px; height: 19px; }
.bk { border: 0; background: none; padding: 8px 0; cursor: pointer; font: 600 13px var(--j-ui); color: var(--j-ink2); white-space: nowrap; }
.flour { display: block; width: 100%; height: 10px; margin: 4px 0 0; color: var(--j-hand); }
.ink { fill: none; stroke: currentColor; stroke-linecap: round; }
.rep { position: relative; margin: 16px 0 0 58px; font: 500 18px/1.55 var(--j-serif); color: var(--j-ink); white-space: pre-wrap; }
.desk .rep { margin-left: 70px; }
.lines { margin: 14px 0 0 58px; padding-left: 18px; display: grid; gap: 10px; font: 500 16px/1.5 var(--j-serif); color: var(--j-ink); }
.desk .lines { margin-left: 70px; }
.lines b { display: block; font: 600 13.5px var(--j-ui); color: var(--j-ink2); }
.lines span { display: block; }
.lines .try { margin-top: 2px; font: 500 14px var(--j-ui); color: var(--j-ink2); }
.lines .try i { font-style: normal; font-weight: 700; color: var(--j-hand); }
</style>
